import translations from "@/config/translations";
import useLangStore from "@/hooks/useLangStore";
import { Table as MuiTable, TableCell as MuiTableCell, Skeleton, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TableColumn<T = any> {
    key: string;
    header: string;
    width?: string;
    render: (row: T, index: number) => ReactNode;
}

export interface TablePaginationProps {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
}

export interface TableProps<T = any> {
    columns: TableColumn<T>[];
    data: T[];
    loading?: boolean;
    rowKey: string | ((row: T) => string);
    onRowClick?: (row: T) => void;
    pagination?: TablePaginationProps;
    emptyMessage?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getPageRange(page: number, total: number): (number | "...")[] {
    if (total <= 1) return [1];
    const pages = new Set<number>();
    pages.add(1);
    pages.add(total);
    if (page > 1) pages.add(page - 1);
    pages.add(page);
    if (page < total) pages.add(page + 1);

    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result: (number | "...")[] = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("...");
        result.push(sorted[i]);
    }
    return result;
}

const SKELETON_COUNT = 6;

// ── SX Styles ─────────────────────────────────────────────────────────────────

const tableContainerSx = {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    scrollbarWidth: "thin",
};

const tableSx = {
    fontFamily: "Alexandria, sans-serif",
    tableLayout: "fixed",
    minWidth: 600,
};

const headerCellSx = (width?: string, isSticky?: boolean) => ({
    fontFamily: "Alexandria, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "var(--color-gray-03)",
    padding: "10px",
    height: "60px",
    borderBottom: "2px solid var(--color-gray-01)",
    borderInlineEnd: "1px solid var(--color-light)",
    background: "var(--color-white)",
    width: width ?? "auto",
    textAlign: "start",
    "&:last-child": { borderInlineEnd: "none" },
    ...(isSticky && {
        position: "sticky",
        insetInlineEnd: 0,
        zIndex: 4,
        boxShadow: "-4px 0 8px rgba(36, 31, 32, 0.06)",
        "[dir='rtl'] &": { boxShadow: "4px 0 8px rgba(36, 31, 32, 0.06)" },
    }),
});

const bodyCellSx = (isSticky?: boolean) => ({
    fontFamily: "Alexandria, sans-serif",
    padding: "10px",
    height: "50px",
    borderBottom: "1.5px solid var(--color-light)",
    borderInlineEnd: "1px solid var(--color-light)",
    background: "var(--color-white)",
    transition: "background 0.12s",
    textAlign: "start",
    "&:last-child": { borderInlineEnd: "none" },
    ...(isSticky && {
        position: "sticky",
        insetInlineEnd: 0,
        zIndex: 1,
        boxShadow: "-4px 0 8px rgba(36, 31, 32, 0.06)",
        "[dir='rtl'] &": { boxShadow: "4px 0 8px rgba(36, 31, 32, 0.06)" },
    }),
});

const rowSx = (clickable?: boolean) => ({
    "&:last-child td": { borderBottom: "none" },
    ...(clickable && {
        cursor: "pointer",
        "&:hover td": { background: "var(--color-light)" },
    }),
});

const skeletonSx = {
    borderRadius: "var(--radius-lev3)",
};

// ── Component ─────────────────────────────────────────────────────────────────

function Table<T extends Record<string, any>>({ columns, data, loading, rowKey, onRowClick, pagination, emptyMessage }: TableProps<T>) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];

    const getKey = (row: T, i: number) => (typeof rowKey === "function" ? rowKey(row) : ((row[rowKey] as string) ?? String(i)));

    const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1;

    const [sizeInput, setSizeInput] = useState(String(pagination?.pageSize ?? 12));
    useEffect(() => {
        setSizeInput(String(pagination?.pageSize ?? 12));
    }, [pagination?.pageSize]);

    const handleSizeBlur = () => {
        const parsed = parseInt(sizeInput, 10);
        const current = pagination?.pageSize ?? 12;
        if (!isNaN(parsed) && parsed >= 5 && parsed !== current) {
            pagination?.onPageSizeChange?.(Math.min(100, parsed));
        } else {
            setSizeInput(String(current));
        }
    };

    return (
        <div className="table-wrap">
            <div className="table">
                <TableContainer sx={tableContainerSx}>
                    <MuiTable sx={tableSx} stickyHeader>
                        {/* Header */}
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <MuiTableCell key={col.key} sx={headerCellSx(col.width, col.key === "actions")}>
                                        {col.header}
                                    </MuiTableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Body */}
                        <TableBody>
                            {loading ? (
                                Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                    <TableRow key={i}>
                                        {columns.map((col) => (
                                            <MuiTableCell key={col.key} sx={bodyCellSx(col.key === "actions")}>
                                                <Skeleton variant="rectangular" height={18} sx={skeletonSx} />
                                            </MuiTableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : data.length === 0 ? (
                                <TableRow>
                                    <MuiTableCell
                                        colSpan={columns.length}
                                        sx={{
                                            textAlign: "center",
                                            height: 200,
                                            color: "var(--color-gray-02)",
                                            fontSize: "14px",
                                            fontFamily: "Alexandria, sans-serif",
                                        }}
                                    >
                                        {emptyMessage ?? t.table.empty}
                                    </MuiTableCell>
                                </TableRow>
                            ) : (
                                data.map((row, i) => (
                                    <TableRow key={getKey(row, i)} sx={rowSx(!!onRowClick)} onClick={onRowClick ? () => onRowClick(row) : undefined}>
                                        {columns.map((col) => (
                                            <MuiTableCell key={col.key} sx={bodyCellSx(col.key === "actions")}>
                                                {col.render(row, i)}
                                            </MuiTableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </MuiTable>
                </TableContainer>

                {/* Pagination */}
                {pagination && !loading && (
                    <div className="table-pagination">
                        <div className="table-pagination-badge">
                            <span className="table-pagination-badge-count">
                                {pagination.pageSize * (pagination.page - 1) + Math.min(pagination.pageSize, data.length)}
                            </span>
                            <span className="table-pagination-badge-sep">{t.table.itemsOf}</span>
                            <span className="table-pagination-badge-total">{pagination.total}</span>
                        </div>

                        <div className="table-pagination-nav">
                            <button
                                type="button"
                                className={`table-pagination-arrow table-pagination-arrow--prev${pagination.page <= 1 ? " table-pagination-arrow--disabled" : ""}`}
                                onClick={() => pagination.page > 1 && pagination.onPageChange(pagination.page - 1)}
                                aria-label="Previous page"
                            >
                                <img src="/icons/arrow-dark-icon.svg" alt="" />
                            </button>

                            <div className="table-pagination-pages">
                                {getPageRange(pagination.page, totalPages).map((item, idx) =>
                                    item === "..." ? (
                                        <span key={`ellipsis-${idx}`} className="table-pagination-ellipsis">
                                            ...
                                        </span>
                                    ) : (
                                        <button
                                            key={item}
                                            type="button"
                                            className={`table-pagination-page${item === pagination.page ? " table-pagination-page--active" : ""}`}
                                            onClick={() => item !== pagination.page && pagination.onPageChange(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            </div>

                            <button
                                type="button"
                                className={`table-pagination-arrow table-pagination-arrow--next${pagination.page >= totalPages ? " table-pagination-arrow--disabled" : ""}`}
                                onClick={() => pagination.page < totalPages && pagination.onPageChange(pagination.page + 1)}
                                aria-label="Next page"
                            >
                                <img src="/icons/arrow-dark-icon.svg" alt="" />
                            </button>
                        </div>

                        {pagination.onPageSizeChange && (
                            <div className="table-pagination-size">
                                <input
                                    type="number"
                                    className="table-pagination-size-input"
                                    value={sizeInput}
                                    min={5}
                                    max={100}
                                    onChange={(e) => setSizeInput(e.target.value)}
                                    onBlur={handleSizeBlur}
                                    onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
                                />
                                <span className="table-pagination-size-label">{t.table.perPage}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Table;
